/**
 * The open inspector panel, as a URL parameter.
 *
 * ## Why the panel needs one
 *
 * With "open clicks in a side panel" on, clicking a subject changed the whole
 * screen and not the address. Three things followed, and an analyst hits all
 * three in the first minute:
 *
 *  - **Back left the page.** The browser's back button is how everyone closes
 *    an overlay, and here it threw away the list behind it instead.
 *  - **The panel could not be sent to anyone.** "Look at this address in the
 *    context of this filtered feed" had no address to paste.
 *  - **A reload lost it**, as did anything that re-mounted the page.
 *
 * ## Only a SUBJECT can be a parameter
 *
 * The panel shows four kinds of target ({@link InspectorTarget}). Two of them —
 * an address and a transaction — are SUBJECTS: they name something that exists
 * independently of this page, so a link to one can be resolved anywhere. The
 * other two — an edge, a node, a collection — are derived from a graph SCENE
 * that only exists in the memory of the page that drew it. A URL naming one
 * would be a link that cannot be opened, which is worse than no link.
 *
 * So encoding is deliberately partial: {@link inspectRef} returns `null` for a
 * scene target, and the opener's rule is that opening one CLEARS the parameter
 * rather than writing a lie into it.
 *
 * ## The colon problem
 *
 * An address subject is a (chain, address) pair and the chain is CAIP-2 —
 * `eip155:1` — which already contains the separator. Parsing therefore splits
 * from the RIGHT: everything before the last colon is the chain, everything
 * after is the address, which holds because no address family uses one. The
 * same trick `parse_transaction_ref` uses on the backend; see
 * [[route-params-are-refs-not-ids]].
 */
import { isTxTenancyScopeId } from "@authe/fe-commons/tenancy";
import type { InspectorContext, InspectorFinding, InspectorSubject, InspectorTarget } from "./model";
import { contextRoute, parseContextSubject } from "./contextSubject";
import { findingRoute } from "@/features/findings/findingRoute";

/** The search-param name. One word, because it appears in links people paste. */
export const INSPECT_PARAM = "inspect";

/**
 * "A panel is open on something this URL cannot name."
 *
 * The scene targets above still need a history entry, or the Back button holds
 * on some panels and not others — which is the same unlearnable rule the
 * side-panel preference was built to avoid. Measured in a case: click an address
 * node, Back closes the panel; click the edge beside it, Back does nothing at
 * all, because the URL either side of it is identical.
 *
 * So a scene target writes this instead. It gives Back an entry to consume and
 * claims nothing it cannot deliver: it names no subject, so
 * {@link subjectFromInspectRef} returns `null` for it exactly as for any other
 * unusable value, and a link carrying it opens the page with no panel — the
 * binding strips it on arrival rather than leaving a word in the address that
 * means nothing to whoever it was sent to.
 */
export const INSPECT_ELEMENT = "element";

const TX_PREFIX = "tx:";
const SCOPED_TX_PREFIX = "scoped-tx:";
const ADDRESS_PREFIX = "addr:";
const FINDING_PREFIX = "finding:";

/** The targets a URL can name: the attributable subjects and a finding. */
export type InspectableRef = InspectorSubject | InspectorFinding | InspectorContext;

/**
 * This target as a parameter value, or `null` when it cannot be one.
 *
 * `null` is a real answer, not a failure: an edge, a node and a collection are
 * scene-derived (see the module note), and the caller's job on `null` is to drop
 * the parameter, not to invent one.
 */
export function inspectRef(target: InspectorTarget | null | undefined): string | null {
  // `null` here means "not linkable", which is a different question from "should
  // there be a history entry" — see `INSPECT_ELEMENT` and `useInspectorUrl`.
  if (!target) return null;
  if (target.kind === "context") {
    const subject = parseContextSubject(target.subject);
    return subject ? `context:${JSON.stringify(subject)}` : null;
  }
  if (target.kind === "transaction") {
    if (!target.canonicalTxId) return null;
    return target.scope
      ? `${SCOPED_TX_PREFIX}${target.scope}:${target.canonicalTxId}`
      : `${TX_PREFIX}${target.canonicalTxId}`;
  }
  if (target.kind === "address") {
    // Both halves required. An address with no chain is not inspectable at all
    // — `opensAddressInPanel` already navigates instead — so a half-built ref
    // could only ever restore an empty panel.
    if (!target.chainId || !target.address) return null;
    return `${ADDRESS_PREFIX}${target.chainId}:${target.address}`;
  }
  if (target.kind === "finding") {
    // A finding id has no colons, so the tier goes in front of it: an empty
    // tier reads as the platform's, which `findingRoute` already defaults.
    if (!target.id) return null;
    return `${FINDING_PREFIX}${(target.scope ?? "").toLowerCase()}:${target.id}`;
  }
  return null;
}

/**
 * The subject a parameter names, or `null` when it names nothing usable.
 *
 * Tolerant on purpose. This value is typed by hand, pasted, truncated by chat
 * clients and left behind by older links, and the page behind it is perfectly
 * readable without a panel — so a value that does not parse is ignored, never
 * an error.
 */
export function subjectFromInspectRef(ref: unknown): InspectableRef | null {
  if (typeof ref !== "string" || !ref) return null;
  if (ref.startsWith("context:")) {
    if (ref.length > 2048) return null;
    try { const subject = parseContextSubject(JSON.parse(ref.slice(8))); return subject ? { kind: "context", subject } : null; }
    catch { return null; }
  }
  if (ref.startsWith(FINDING_PREFIX)) {
    const rest = ref.slice(FINDING_PREFIX.length);
    const split = rest.indexOf(":");
    const scope = split >= 0 ? rest.slice(0, split) : "";
    const id = split >= 0 ? rest.slice(split + 1) : rest;
    return id ? { kind: "finding", id, ...(scope ? { scope } : {}) } : null;
  }

  if (ref.startsWith(SCOPED_TX_PREFIX)) {
    const rest = ref.slice(SCOPED_TX_PREFIX.length);
    const split = rest.indexOf(":");
    const scope = rest.slice(0, split);
    const canonicalTxId = rest.slice(split + 1);
    return split > 0 && isTxTenancyScopeId(scope) && canonicalTxId
      ? { kind: "transaction", canonicalTxId, scope } : null;
  }

  if (ref.startsWith(TX_PREFIX)) {
    const canonicalTxId = ref.slice(TX_PREFIX.length);
    return canonicalTxId ? { kind: "transaction", canonicalTxId } : null;
  }

  if (ref.startsWith(ADDRESS_PREFIX)) {
    const rest = ref.slice(ADDRESS_PREFIX.length);
    const split = rest.lastIndexOf(":");
    // No colon at all means no chain — and an address without one cannot open
    // a panel (the panel resolves attribution per chain), so there is nothing
    // to restore.
    if (split <= 0) return null;
    const chainId = rest.slice(0, split);
    const address = rest.slice(split + 1);
    return chainId && address ? { kind: "address", chainId, address } : null;
  }

  return null;
}

/**
 * The route this subject has a page of its own at.
 *
 * What a `?inspect=` link means to somebody whose preference is OFF. The link
 * says "look at this subject"; their way of working says subjects open as
 * pages. Answering with the page is the same information in the form they
 * chose — and it is why a shared panel link is not a dead end for them, which
 * silently ignoring the parameter would be.
 */
export function subjectRoute(
  subject: InspectableRef,
): { to: string; params?: Record<string, string>; search?: Record<string, string> } | null {
  if (subject.kind === "context") return contextRoute(subject.subject);
  if (subject.kind === "finding") {
    const route = findingRoute(subject.id, subject.scope);
    return subject.id ? { to: route.to, params: route.params, search: route.search } : null;
  }
  if (subject.kind === "transaction") {
    return subject.canonicalTxId
      ? { to: "/transactions/$id", params: { id: subject.canonicalTxId }, ...(subject.scope ? { search: { scope: subject.scope } } : {}) }
      : null;
  }
  if (subject.kind === "address") {
    return subject.address
      ? {
          to: "/addresses/$id",
          params: { id: subject.address },
          ...(subject.chainId ? { search: { chain: subject.chainId } } : {}),
        }
      : null;
  }
  return null;
}
